import _ from "lodash";
import { MessageCircle } from "lucide-react-native";
import { Box, IconButton, useTheme } from "native-base";
import React, { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useSelector } from "react-redux";

import { Routes } from "../constants";
import analyticsInterface from "../helpers/analyticsHelpers";
import { openURL } from "../helpers/generalHelpers";
import { shouldShowCustomIntercomLauncher } from "../helpers/userHelpers";
import type { User } from "../services/backendTypes";
import { userSelector } from "../slices/userSlice";

const { openIntercomMessenger } = analyticsInterface;

// Constants
const SUPPORT_WEBSITE = "https://help.nutrition-app.com/";
const INTERCOM_LOAD_TIMEOUT_MS = 5 * 1000; // Wait 5 seconds for Intercom to load

interface SupportButtonProps {
  currentRouteName: string | null;
}

const SupportButton: React.FC<SupportButtonProps> = ({ currentRouteName }) => {
  const theme = useTheme();
  const user = useSelector(userSelector);
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    // Check if Intercom is already loaded
    const checkIntercomLoaded = (): boolean =>
      typeof window !== "undefined" && window.Intercom && _.isFunction(window.Intercom);

    // Initial check
    if (checkIntercomLoaded()) {
      setIntercomLoaded(true);
      return undefined;
    }

    // Poll for Intercom to load
    const pollInterval = setInterval(() => {
      if (checkIntercomLoaded()) {
        setIntercomLoaded(true);
        clearInterval(pollInterval);
        clearTimeout(fallbackTimeout);
      }
    }, 500); // Check every 500ms

    // After timeout, show button anyway but use fallback behavior
    const fallbackTimeout = setTimeout(() => {
      setShowFallback(true);
      clearInterval(pollInterval);
    }, INTERCOM_LOAD_TIMEOUT_MS);

    // Cleanup
    return () => {
      clearInterval(pollInterval);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  // Only show on mobile web viewports
  if (Platform.OS !== "web") {
    return null;
  }

  // Check if viewport is mobile/tablet size
  const isMobileViewport = typeof window !== "undefined" && window.innerWidth <= 1024;

  if (!isMobileViewport) {
    return null;
  }

  // Define allowed screens for the Intercom launcher
  const allowedScreens = [
    Routes.LoginStack, // Login nested stack containing LoginScreen, MagicLinkSentScreen, EnterOTPScreen
    Routes.LoginScreen,
    Routes.EnterOTPScreen,
    Routes.MyProfileScreen,
  ];

  // Check if current screen is allowed
  if (!currentRouteName || !allowedScreens.includes(currentRouteName as Routes)) {
    return null;
  }

  // For login screens, show the launcher regardless of user state
  const isLoginScreen =
    (currentRouteName as Routes) === Routes.LoginStack ||
    (currentRouteName as Routes) === Routes.LoginScreen ||
    (currentRouteName as Routes) === Routes.EnterOTPScreen;

  // Check if the custom launcher should be shown for this user
  // On login screens, always show; on other screens, check user permissions
  if (!isLoginScreen && !shouldShowCustomIntercomLauncher(user)) {
    return null;
  }

  // Don't show button until either Intercom loads or we hit the fallback timeout
  if (!intercomLoaded && !showFallback) {
    return null;
  }

  const handlePress = (): void => {
    if (intercomLoaded) {
      // Intercom is loaded, use it
      (openIntercomMessenger as () => void)();
    } else {
      // Fallback: Open support website directly
      window.open(SUPPORT_WEBSITE, "_blank");
      openURL(SUPPORT_WEBSITE);
    }
  };

  return (
    <Box
      position="fixed"
      bottom="120px"
      left="20px"
      zIndex={9999}
      // Ensure button stays above other content but below modals
    >
      <IconButton
        icon={<MessageCircle size={24} color="white" />}
        borderRadius={theme.borderRadii.button.pill}
        bg="primary.50"
        _hover={{
          bg: "primary.60",
        }}
        _pressed={{
          bg: "primary.70",
        }}
        shadow={3}
        onPress={handlePress}
        p={3}
        accessibilityLabel="Open support chat"
      />
    </Box>
  );
};

export default SupportButton;
